import { cached } from '@glimmer/tracking';

import type { DataLabelPart, DSData } from 'webapp-types';
import { DataLabelType } from 'webapp/enums/enums';
import {
	buildDataLabelPartsFromDimensionsAndValuesMap,
	equal,
	glueDimensionValues,
	uniq,
} from 'webapp/utils/common';
import { simpleFormatCharacters, sumValues } from 'webapp/utils/number';

import type DsDataPointBase from 'webapp/models-custom/ds-data/point/base';
import type DsDataPointWithTs from 'webapp/models-custom/ds-data/point/with-ts';
import type DataRootBase from 'webapp/models-custom/ds-data/root/base';

export default abstract class DsDataOutputBase {
	abstract dataRoot: DataRootBase;
	abstract points: DsDataPointBase[];

	dimensionValues: DSData.DimensionsAndValuesMap;
	unit: string;

	constructor(rawData: DSData.PointGroup) {
		this.dimensionValues = rawData.dimensionValues || {};
		this.unit = rawData.unit || '';
	}

	get dateRange() {
		return this.dataRoot.dateRange;
	}

	get rootFormat() {
		return this.dataRoot.format;
	}

	get format() {
		// the actual format of this output including the unit
		// this will be passed to the points themselves in case valueStr needs to be calculated
		const format = this.rootFormat || '';
		if (!this.unit) {
			// no unit - no problems
			return format;
		}
		// if format has percentage then don't add unit
		if (format.includes('%')) {
			return format;
		}
		const hasSpecialChars = format
			.split('')
			.some((char) => !simpleFormatCharacters.includes(char));
		if (hasSpecialChars) {
			// assume format is custom or already has the unit somewhere
			return format;
		}
		// if ds format is set without unit sign, don't add unit
		const dsOverrideFormat = this.dataRoot.dataSelect.overrides.format;
		if (dsOverrideFormat && !dsOverrideFormat.includes('�')) {
			return format;
		}
		// use prefix if the unit � sign is at the start, or we have common currencies
		// the default is postifx
		const usePrefix =
			dsOverrideFormat?.startsWith('�') || ['$', '€', '£', 'EUR', 'USD'].includes(this.unit);

		// prettify common units
		let unitToUse = this.unit;
		if (unitToUse === 'USD') unitToUse = '$';
		if (unitToUse === 'EUR') unitToUse = '€';

		const formatWithUnit = usePrefix ? `${unitToUse} ${format}` : `${format} ${unitToUse}`;

		return formatWithUnit;
	}

	get scale() {
		return this.dataRoot.scale;
	}

	get invert() {
		return this.dataRoot.invert;
	}

	get isDataIncomplete() {
		return this.dataRoot.isDataIncomplete;
	}

	// for media creatives images loading status
	get isMediaLoading() {
		return this.dataRoot.isMediaLoading;
	}

	get metricName() {
		return this.dataRoot.metricName;
	}

	get metricNameOverride() {
		return this.dataRoot.metricNameOverride;
	}

	get isOneSpecificAttributeOnSetup() {
		return this.dataRoot.isOneSpecificAttributeOnSetup;
	}

	get sourceName() {
		return this.dataRoot.sourceName;
	}

	get sourceAvatar() {
		return this.dataRoot.sourceAvatar;
	}

	get isSourceDataset() {
		return this.dataRoot.isSourceDataset;
	}

	get fullTitle() {
		// always with unit - used in tooltips
		let title = this.titleWithoutUnit;
		if (this.unit) {
			title += ` / ${this.unit}`;
		}
		return title;
	}

	get titleWithoutMetric() {
		const chunks: string[] = [];
		const attributeNames = this.attributeNamesString;
		if (attributeNames) {
			chunks.push(attributeNames);
		}
		if (this.unit) {
			chunks.push(this.unit);
		}
		return chunks.join(' / ');
	}

	get titleWithoutAttribute() {
		return this.addUnitToTitleIfEligible(this.metricName);
	}

	get titleWithoutUnit() {
		let title = this.metricName;
		const attributeNames = this.attributeNamesString;
		if (attributeNames) {
			title += ` / ${attributeNames}`;
		}
		return title;
	}

	get hasAttributeNames() {
		return Object.values(this.dimensionValues).length > 0;
	}

	@cached
	get attributeNames() {
		return uniq(Object.values(this.dimensionValues));
	}

	get attributeNamesString() {
		return glueDimensionValues(this.dimensionValues);
	}

	@cached
	get isUnitShownInTitle() {
		return !!this.dataRoot.dataSelect?.isBlockDataDifferentUnits;
	}

	get shortTitle() {
		return this.addUnitToTitleIfEligible(this.shortTitleWithoutUnit);
	}

	get shortTitleWithoutUnit() {
		// rules: https://app.asana.com/0/694429864926442/1205936843026973/f
		if (this.isOneSpecificAttributeOnSetup) {
			// if one specific dimension value chosen, show only metric name
			return this.metricName;
		} else if (this.hasAttributeNames) {
			const attributeNames = this.attributeNamesString;
			// will have multiple attributes. in case of title override, show it, otherwise just dimension
			if (this.metricNameOverride) {
				return `${this.metricNameOverride} / ${attributeNames}`;
			} else {
				return attributeNames;
			}
		} else {
			// fallback to metric name (is either overriden or default)
			return this.metricName;
		}
	}

	@cached
	get dataLabelParts(): DataLabelPart[] {
		if (this.isOneSpecificAttributeOnSetup) {
			return [{ type: DataLabelType.METRIC, value: this.metricName }];
		}
		if (this.hasAttributeNames) {
			const dimParts = buildDataLabelPartsFromDimensionsAndValuesMap(
				this.dimensionValues,
				this.dataRoot.dimensions,
			);
			if (this.metricNameOverride) {
				return [
					{ type: DataLabelType.METRIC, value: this.metricNameOverride },
					...dimParts,
				];
			}
			return dimParts;
		}
		return [{ type: DataLabelType.METRIC, value: this.metricName }];
	}

	@cached
	get totalSumValue(): number | null {
		return sumValues(this.points.map((p) => p.value));
	}

	@cached
	get otherOutput() {
		return this.dataRoot.outputsOther.find((outputOther) => {
			return outputOther.isMatchForOutput(this);
		});
	}

	getPointForIndex(index: number) {
		return (this.points as DsDataPointWithTs[]).find((item) => item.index === index) || null;
	}

	// Strict: dimension KEY *and* value must match (order-independent). Use when comparing
	// outputs WITHIN a single metric (per-metric grouping, current↔compare/goal/"other"),
	// where every output shares the same dimension key.
	equalDimensionValues(dimensionValues: DSData.DimensionsAndValuesMap) {
		return equal(this.dimensionValues, dimensionValues, true);
	}

	// Value-only: match on dimension VALUE(s), ignoring the key. Use when joining outputs
	// ACROSS different metrics (table metrics-as-columns rows, funnel series across steps),
	// because each metric may label the same value under a different key. Restores the
	// pre-multidimensioning "match by attribute value" behavior.
	equalAttributeNames(dimensionValues: DSData.DimensionsAndValuesMap) {
		return this.attributeNamesString === glueDimensionValues(dimensionValues);
	}

	private addUnitToTitleIfEligible(title: string) {
		if (this.unit && this.isUnitShownInTitle) {
			title += ` / ${this.unit}`;
		}
		return title;
	}
}
